import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { mdiPlus, mdiTrashCanOutline } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { ApiSearchableSelect, type ApiSearchableSelectOption } from '../ApiSearchableSelect'
import { usePropertyDefinitionSearch } from '../../hooks/usePropertyDefinitionSearch'
import { useCan } from '../../contexts/PermissionsContext'

/** The six leaf-condition operators the backend's `applyPropertyFilter` supports. */
export type AggregationConditionOp = 'eq' | 'neq' | 'gt' | 'lt' | 'is_set' | 'is_empty'

const ALL_OPS: AggregationConditionOp[] = ['eq', 'neq', 'gt', 'lt', 'is_set', 'is_empty']
const VALUE_OPS: AggregationConditionOp[] = ['eq', 'neq', 'gt', 'lt']

/** Exactly the leaf JSON shape `applyPropertyFilter` (internal/handlers/aggregations/service.go) supports. */
export interface AggregationCondition {
  property: string
  op: AggregationConditionOp
  value?: string
}

function isValueOp(op: AggregationConditionOp): boolean {
  return VALUE_OPS.includes(op)
}

/** True when a condition (or the absence of one) is ready to submit. */
export function isConditionComplete(condition: AggregationCondition | null): boolean {
  if (!condition) return true
  if (!condition.property || !condition.op) return false
  if (isValueOp(condition.op) && (condition.value === undefined || condition.value === '')) return false
  return true
}

interface AggregationConditionEditorProps {
  org: string
  label: string
  helpText?: string
  value: AggregationCondition | null
  onChange: (value: AggregationCondition | null) => void
  'data-testid': string
}

/**
 * Structured editor for a single aggregation leaf condition
 * (`{"property", "op", "value"}`). The condition itself is optional — when
 * `value` is null, "all children match" and only an "add condition"
 * affordance is shown.
 */
export function AggregationConditionEditor({
  org,
  label,
  helpText,
  value,
  onChange,
  'data-testid': testIdPrefix,
}: AggregationConditionEditorProps) {
  const { t } = useTranslation('aggregations')
  const { searchPropertyDefinitions, initialOptions, propertyDefinitionsAll } = usePropertyDefinitionSearch(org)
  // A condition is part of an aggregation definition. Without
  // `configuration.write` the condition stays readable but the add/remove
  // affordances go and the controls render read-only (spec 2026-09-04-02).
  const canWriteConfiguration = useCan('configuration.write')

  const selectedPropertyUid = useMemo(() => {
    if (!value?.property) return ''
    return propertyDefinitionsAll.find(pd => pd.code === value.property)?.uid || ''
  }, [propertyDefinitionsAll, value?.property])

  const handleAdd = useCallback(() => {
    onChange({ property: '', op: 'eq', value: '' })
  }, [onChange])

  const handleRemove = useCallback(() => {
    onChange(null)
  }, [onChange])

  const handlePropertySelect = useCallback((option: ApiSearchableSelectOption) => {
    const code = (option.data?.code as string) || ''
    onChange({
      property: code,
      op: value?.op || 'eq',
      value: isValueOp(value?.op || 'eq') ? (value?.value ?? '') : undefined,
    })
  }, [onChange, value])

  const handleOpChange = useCallback((op: string) => {
    if (!value) return
    const nextOp = op as AggregationConditionOp
    onChange({
      property: value.property,
      op: nextOp,
      value: isValueOp(nextOp) ? (value.value ?? '') : undefined,
    })
  }, [value, onChange])

  const handleValueChange = useCallback((raw: string) => {
    if (!value) return
    onChange({ ...value, value: raw })
  }, [value, onChange])

  if (!value) {
    return (
      <div>
        <div className="flex items-center justify-between">
          <Label>{label}</Label>
          {canWriteConfiguration && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAdd}
              data-testid={`${testIdPrefix}-add`}
            >
              <AppIcon path={mdiPlus} size="xs" className="mr-1" />
              {t('conditions.add_condition')}
            </Button>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-1">{helpText || t('conditions.no_condition_help')}</p>
      </div>
    )
  }

  const needsValueInput = isValueOp(value.op)

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <Label>{label}</Label>
        {canWriteConfiguration && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={handleRemove}
            aria-label={t('conditions.remove_condition')}
            title={t('conditions.remove_condition')}
            data-testid={`${testIdPrefix}-remove`}
          >
            <AppIcon path={mdiTrashCanOutline} size="xs" />
          </Button>
        )}
      </div>
      <div className={needsValueInput ? 'grid grid-cols-1 sm:grid-cols-3 gap-2' : 'grid grid-cols-1 sm:grid-cols-2 gap-2'}>
        <ApiSearchableSelect
          value={selectedPropertyUid}
          placeholder={t('conditions.property_placeholder')}
          searchPlaceholder={t('conditions.property_search_placeholder')}
          onSearch={searchPropertyDefinitions}
          onOptionSelect={handlePropertySelect}
          initialOptions={initialOptions}
          disabled={!canWriteConfiguration}
          data-testid={`${testIdPrefix}-property`}
        />
        <Select value={value.op} onValueChange={handleOpChange} disabled={!canWriteConfiguration}>
          <SelectTrigger data-testid={`${testIdPrefix}-op`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ALL_OPS.map(op => (
              <SelectItem key={op} value={op}>{t(`conditions.op.${op}`)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {needsValueInput && (
          <Input
            value={value.value ?? ''}
            onChange={(e) => handleValueChange(e.target.value)}
            placeholder={t('conditions.value_placeholder')}
            readOnly={!canWriteConfiguration}
            data-testid={`${testIdPrefix}-value`}
          />
        )}
      </div>
      {helpText && <p className="text-xs text-muted-foreground mt-1">{helpText}</p>}
    </div>
  )
}
